import { TrendingUp, Target, Shield, Zap, Brain } from "lucide-react";

export type Model = {
  name: string;
  description: string;
  type: string;
  success_rate: number;
  monthly_return: number;
  risk_per_day?: number;
  target_per_day?: number;
  score: number;
  risk_level: string;
  max_drawdown: number;
  sharpe_ratio: number;
  required_amount: number;
};

export function useModelInfoModal() {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => new Intl.NumberFormat("en-IN").format(num);

  const getModelIcon = (modelType: string): React.ElementType => {
    switch (modelType) {
      case "momentum":
        return TrendingUp;
      case "mean_reversion":
        return Target;
      case "breakout":
        return Zap;
      case "volatility":
        return Shield;
      default:
        return Brain;
    }
  };

  const getModelIconColor = (modelType: string) => {
    switch (modelType) {
      case "momentum":
        return "text-accent-300";
      case "mean_reversion":
        return "text-green-300";
      case "breakout":
        return "text-purple-300";
      case "volatility":
        return "text-orange-300";
      default:
        return "text-slate-400";
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "low":
        return "text-green-300 bg-green-500/15";
      case "medium":
        return "text-yellow-300 bg-yellow-500/15";
      case "high":
        return "text-red-300 bg-red-500/15";
      default:
        return "text-slate-300 bg-white/10";
    }
  };

  return { formatPercentage, formatCurrency, formatNumber, getModelIcon, getModelIconColor, getRiskLevelColor };
}

export const getEntryCriteria = (modelType: string) => {
  switch (modelType) {
    case "momentum":
      return "RSI oversold/overbought, MACD crossover, volume confirmation";
    case "mean_reversion":
      return "Bollinger Band touches, Stochastic extremes, price deviation from mean";
    case "breakout":
      return "Price breaks resistance/support with volume, consolidation pattern completion";
    case "volatility":
      return "VIX spike, options skew, volatility contraction/expansion";
    default:
      return "Technical indicator confluence, price action confirmation";
  }
};

export const getExitCriteria = (modelType: string) => {
  switch (modelType) {
    case "momentum":
      return "RSI reaches opposite extreme, MACD divergence, trend reversal signals";
    case "mean_reversion":
      return "Price returns to mean, Bollinger Band center touch, momentum exhaustion";
    case "breakout":
      return "Target reached, false breakout confirmation, trend exhaustion";
    case "volatility":
      return "Volatility normalization, options premium decay, mean reversion";
    default:
      return "Take profit hit, stop loss triggered, technical reversal signals";
  }
};

export const getRiskManagement = (modelType: string) => {
  switch (modelType) {
    case "momentum":
      return "2-3% stop loss, trailing stops, position sizing based on volatility";
    case "mean_reversion":
      return "1-2% stop loss, tight position management, correlation checks";
    case "breakout":
      return "3-4% stop loss, volume confirmation, false breakout protection";
    case "volatility":
      return "2-3% stop loss, options hedging, volatility-based sizing";
    default:
      return "2-3% stop loss, portfolio diversification, correlation limits";
  }
};

export const getMarketConditions = (modelType: string) => {
  switch (modelType) {
    case "momentum":
      return "Trending markets, high volume periods, clear directional moves";
    case "mean_reversion":
      return "Ranging markets, low volatility, sideways price action";
    case "breakout":
      return "High volatility, news catalysts, consolidation breakouts";
    case "volatility":
      return "Market stress, earnings season, geopolitical events";
    default:
      return "Normal market conditions with clear technical patterns";
  }
};
